<template>
    <b-container
      class="reader-card text-center px-0 avoid-break"
      fluid
      :class="heightMultiplierByFont"
    >
      <b-row
        class="reader-card__dropdown-wrapper"
        no-gutters
        :class="dropdownWrapperVisible"
        :align-h="alignHandler(!variable.visibility)"
      >
        <i
          aria-hidden="true"
          v-if="!variable.visibility"
          class="fas fa-eye-slash ml-3 mt-3"
          v-b-tooltip.hover.top="$t('supervision.hidden-variable-tooltip')"
        />
        <b-dropdown
          v-if="$hasUserPermission('PER015') || $hasUserPermission('PER016')"
          no-caret
          right
          size="lg"
          variant="link"
          boundary="viewport"
          :popper-opts="this.popperOptions"
          @shown="isVisible(true)"
          @hidden="isVisible(false)"
        >
          <template slot="button-content">
            <s-icon
              class="pb-1"
              name="options-menu"
            />
          </template>
          <b-dropdown-item
            v-if="$hasUserPermission('PER015')"
            class="reader-card__dropdown"
            @click.prevent.stop="renameVariable"
          >
            <s-icon
              name="edit"
              class="mr-2"
            />
            {{ $t('actions.rename') }}
          </b-dropdown-item>
          <b-dropdown-divider v-if="$hasUserPermission('PER015')"/>
          <b-dropdown-item
            v-if="$hasUserPermission('PER015')"
            class="reader-card__dropdown"
            :disabled="variable.hasOriginalName"
            @click="resetVariable(variable)"
          >
            <i
              aria-hidden="true"
              class="fas fa-redo-alt mr-2"
            />
            {{ $t('actions.reset-name') }}
          </b-dropdown-item>
          <b-dropdown-divider v-if="$hasUserPermission('PER015') && $hasUserPermission('PER016')"/>
          <b-dropdown-item
            v-if="$hasUserPermission('PER016')"
            class="reader-card__dropdown"
            @click="handleExhibition"
          >
            <i
              aria-hidden="true"
              class="mr-2"
              :class="visibilityIconClass"
            />
            {{ exhibitionLabel(!variable.visibility) }}
          </b-dropdown-item>
          <b-dropdown-divider v-if="showMoveOption && $hasUserPermission('PER015')"  />
          <b-dropdown-item
            v-if="showMoveOption && $hasUserPermission('PER015')"
            class="reader-card__dropdown"
          >
            <ul class="reader-card__menu">
              <li
                @click="showGroupListHandler()"
                @click.prevent.stop
              >
                <s-icon
                  :name="arrowIcon"
                  class="mr-2"
                />
                {{ $t('actions.move-to') }}
                <ul
                  v-if="showGroupList"
                  @click="showGroupListHandler()"
                >
                  <b-list-group class="mt-2">
                    <template v-for="({ id, name }) in groupList">
                      <b-list-group-item
                        button
                        :key="id"
                        v-if="hideListItem(id)"
                        @click="changeVariableGroup(id)"
                      >
                        {{ name }}
                      </b-list-group-item>
                    </template>
                  </b-list-group>
                </ul>
              </li>
            </ul>
          </b-dropdown-item>
        </b-dropdown>
      </b-row>
      <b-row
        align-h="center"
        no-gutters
        class="reader-card__value"
        :class="disabledClass"
      >
      <b-col v-if="variable.additionalInfo" cols="12" align-self="center">
        <i
          class="material-icons-round reader-card__alarm"
          aria-hidden="true"
          v-b-tooltip.hover.right
          :title="variable.additionalInfo"
        >
          warning_amber
        </i>
      </b-col>
      <b-col v-else cols="12">
        <label class="reader-card__value-wrapper">
          <span
            class="reader-card__value-result"
            :class="getClassIfBigAsString(variable.value)"
          >
            {{ variable.value }}
          </span>
          <span class="reader-card__value-legend">
            {{ variable.measurementUnit }}
          </span>
        </label>
      </b-col>
      </b-row>
      <b-row
        align-h="center"
        no-gutters
        :class="variableNameClasses"
      >
        <b-col
          v-if="!renamingVariable"
          :class="variableNameClasses"
          cols="12"
        >
          <label
            :class="variableNameClasses"
            class="px-2"
          >
            {{ variable.name }}
          </label>
        </b-col>
        <b-col
          v-if="renamingVariable"
          cols="12"
        >
          <b-form-textarea
            autofocus
            ref="name-input"
            no-resize
            rows="2"
            max-rows="15"
            no-auto-shrink
            :maxlength="maxlength"
            v-model="newVariableName"
            @keydown.esc="quitRenaming"
            @blur.prevent.stop="quitRenaming"
            @keydown.enter="submitRenameRequest"
          />
        </b-col>
      </b-row>
    </b-container>
</template>
<script>
import { mapGetters } from 'vuex';
import popperOptsMixin from '@/services/popperOptsMixin';

export default {
  name: 'VariablesCard',
  props: {
    variable: {
      type: Object,
    },
    groupList: {
      type: Array,
    },
    groupId: {
      type: String,
    },
  },
  mixins: [popperOptsMixin],
  data: () => ({
    showGroupList: false,
    renamingVariable: false,
    renamingHold: true,
    newVariableName: null,
    visible: false,
    maxlength: 600,
  }),
  computed: {
    ...mapGetters([
      'currentFontSize',
    ]),
    heightMultiplierByFont() {
      const sizes = {
        'manage-fonts_14px': 'reader-card__proportional-height-normal',
        'manage-fonts_16px': 'reader-card__proportional-height-medium',
        'manage-fonts_18px': 'reader-card__proportional-height-big',
      };
      return sizes[this.currentFontSize];
    },
    visibilityIconClass() {
      return {
        'fas fa-eye-slash': this.variable.visibility,
        'fas fa-eye': !this.variable.visibility,
      };
    },
    disabledClass() {
      return { 'reader-card-disabled': !this.variable.visibility };
    },
    variableNameClasses() {
      return {
        'font-italic': !this.variable.hasOriginalName,
        'reader-card__reset-bottom': this.permissionToReset,
        'reader-card__bottom': !this.permissionToReset,
        ...this.disabledClass,
      };
    },
    truncateText() {
      return this.variable.name.length > 35;
    },
    permissionToReset() {
      return this.variable.showReset;
    },
    arrowIcon() {
      return this.showGroupList ? 'arrow-up' : 'arrow-down';
    },
    showMoveOption() {
      return this.groupList.length > 1;
    },
    dropdownWrapperVisible() {
      return { 'reader-card__dropdown-wrapper--visible': this.visible };
    },
  },
  methods: {
    hideListItem(id) {
      return id !== this.groupId;
    },
    alignHandler(show) {
      return show ? 'between' : 'end';
    },
    exhibitionLabel(show) {
      return show
        ? this.$t('sensors.actions.show-variable') : this.$t('sensors.actions.hide-variable');
    },
    handleExhibition() {
      this.$emit('toggle-variable-visibility', this.variable);
    },
    renameVariable() {
      this.renamingHold = !this.renamingHold;
      this.renamingVariable = true;
      this.newVariableName = this.variable.name;

      this.$nextTick(() => {
        this.$refs['name-input'].focus();
      });
      this.$emit('on-renaming-card', true);
    },
    submitRenameRequest() {
      const { newVariableName, variable } = this;
      if (!newVariableName || newVariableName === variable.name) {
        this.renamingVariable = false;
        this.$emit('on-renaming-card', false);
        return;
      }
      const payload = {
        name: newVariableName,
        id: variable.id,
      };
      this.$emit('rename-variable-request', payload);
      this.renamingVariable = false;
      this.$emit('on-renaming-card', false);
    },
    resetVariable({ id }) {
      this.$emit('reset-variable', id);
    },
    showGroupListHandler() {
      this.showGroupList = !this.showGroupList;
    },
    changeVariableGroup(newGroupId) {
      const { id: variableId, groupSupervisionFieldId } = this.variable;
      const payload = {
        originGroupId: this.groupId,
        destinyGroupId: newGroupId,
        variableId,
        groupSupervisionFieldId,
        order: null,
      };
      this.$emit('change-variable-group-by-button', payload);
    },
    quitRenaming() {
      if (this.renamingHold) {
        this.renamingHold = false;
        return;
      }
      this.renamingVariable = false;
      this.$emit('on-renaming-card', false);
    },
    isVisible(value) {
      this.visible = value;
      if (this.visible === false) this.showGroupList = false;
    },
    getClassIfBigAsString(value) {
      return {
        'reader-card__value-result-big': String(value || '').length > 5,
      };
    },
  },
};
</script>
<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.reader-card
  background: var(--color-background-light)
  border: 1px solid var(--color-border-regular)
  border-radius: 5px
  width: 100%
  height: 100%
  padding: 15px 10px
  display: flex
  flex-direction: column
  justify-content: center
  flex: 1
  position: relative
  &-disabled
    opacity: .7
  &__reset-tooltip-container
    height: 15px
    color: var(--color-info)
  &__dropdown
    color: var(--font-color-primary)
  &__dropdown-wrapper
    position: absolute !important
    right: 0
    top: 0
    z-index: 1
  &__menu
    font-weight: 600
    margin: 0
    padding: 0
    list-style: none
    li
      list-style: none
      min-width: 90%
    li ul
      margin: 0
      padding: 0
      width: 90%
      overflow-y: auto
      max-height: 400px
  &__bottom
    display: flex
    flex-direction: column
    justify-content: center
    word-break: break-word
  &__reset-bottom
    display: flex
    justify-content: center
    word-break: break-word
  &__alarm
    font-size: 70px
  &__value
    &-wrapper
      padding: 0 1.5rem
    &-result
      font-size: 2em
      word-break: break-word
    &-result-big
      font-size: 1.5em
    &-legend
      font-size: 1.5em
  +media-min-sm()
    &__value
      &-result
        font-size: 2.5em
      &-result-big
        font-size: 1.5em
      &-legend
        font-size: 1.5em
  +media-min-md()
    &__value
      &-result
        font-size: 2.3em
      &-result-big
        font-size: 1.6em
      &-legend
        font-size: 1.6em
  +media-min-lg()
    &__value
      &-result
        font-size: 2.6em
      &-result-big
        font-size: 1.8em
      &-legend
        font-size: 1.8em

  .reader-card__alarm
    font-size: 50px

  .reader-card__dropdown-wrapper--visible
    z-index: 2
</style>
